/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import { FormikProps } from 'formik'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Lightbox } from '@nexigendigital/nxui'
import { useAddTwoFactorMutation, useCreateTwoFactorMutation } from 'api/account'

import ConfigureAppAuthForm from 'containers/account/form/configureAppAuthForm'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    status: boolean,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ConfigureAppAuthLightbox({ status, close }: Props) {
    const formRef = useRef<FormikProps<{ code: string }>>(null)
    const [createTwoFactor] = useCreateTwoFactorMutation({ fixedCacheKey: 'create-two-factor-authenticator' })
    const [, { isLoading, isSuccess, isError }] = useAddTwoFactorMutation({ fixedCacheKey: 'add-two-factor-authenticator' })

    /**********************************************************************************************************
     *   SIDE EFFECTS
     **********************************************************************************************************/
    useEffect(() => {
        createTwoFactor({ type: 'authenticator' })
    }, [createTwoFactor])

    useEffect(() => {
        if (isError && formRef.current) {
            formRef.current.setSubmitting(false)
            formRef.current.resetForm()
        }

        if (isSuccess) {
            close()
        }
    }, [isError, isSuccess])

    return (
        <Lightbox
            title={'Authenticator App'}
            description={'Scan the QR Code below with your preferred authenticator app to setup two-factor authentication.'}
            content={<ConfigureAppAuthForm form={formRef}/>}
            conditions={{ state: status, action: () => close() }}
            actions={[
                {
                    label: 'Back',
                    color: 'secondary',
                    type: 'submit',
                    disabled: isLoading,
                    func: () => close()
                },
                {
                    label: 'Add',
                    color: 'primary',
                    type: 'submit',
                    loading: isLoading && 'Adding',
                    form: 'configureAppAuthForm'
                }
            ]}
        />
    )
}
